'use client';

import type { Data } from '@strapi/types';
import { useTranslations } from '@power-core/toolkit';
import type { FunctionComponent } from 'react';

import { AbbMoreDots } from '@/assets/icons/components/abb-more-dots';
import { useOpenSharedDocument } from '@/modules/document/hooks/use-open-shared-document';
import { useDate } from '@/modules/utils/custom-hooks/use-date';
import { useNavigation } from '@/modules/utils/custom-hooks/use-navigation';
import { useTheme } from '@/modules/utils/custom-hooks/use-theme';
import { Dropdown } from '@/ui-components/dropdown/dropdown';
import { Flexbox } from '@/ui-components/flexbox/flexbox';
import { LabelBadge } from '@/ui-components/label-badge/label-badge';
import { Paragraph } from '@/ui-components/paragraph/paragraph';
import { Tooltip } from '@/ui-components/tooltip/tooltip';

export type SharedDocumentProps = {
	documentAccess: Data.Entity<'api::document-access.document-access'>;
};

export const SharedDocument: FunctionComponent<SharedDocumentProps> = ({ documentAccess }) => {
	const t = useTranslations();
	const { addSearchParam } = useNavigation();
	const { format } = useDate();
	const { isDarkTheme } = useTheme();
	const { openSharedDocument } = useOpenSharedDocument({
		accessLevel: documentAccess.accessLevel,
		projectDocumentId: documentAccess.projectDocument?.documentId || '',
	});

	if (!documentAccess || !documentAccess.projectDocument) return null;

	const { name, documentId, documentType, project } = documentAccess.projectDocument;
	const isCurvesDocumentView = documentType?.title === 'T_CURVES' && documentAccess.accessLevel === 'view';

	const menuItems = [
		{
			value: 'LEAVE',
			label: (
				<span className='!text-brand text-sm w-full text-left px-4 py-3' onClick={() => {}}>
					{t('T_LEAVE')}
				</span>
			),
			onClick: () => {
				addSearchParam([
					{ key: 'modal', value: 'Alert' },
					{ key: 'id', value: documentAccess.documentId },
					{ key: 'type', value: 'sharedDocument' },
				]);
			},
		},
	];

	const renderPeriod = () => {
		if (documentAccess.unlimited && !documentAccess.expiresAt) {
			return (
				<Paragraph variant='text-sm' className='text-n500_light_text'>
					{`${t('T_EXPIRE_AT')}: ${t('T_UNLIMITED_INVITATION')}`}
				</Paragraph>
			);
		}

		return (
			<Paragraph variant='text-sm' className='text-n500_light_text'>
				{`${t('T_EXPIRE_AT')}: ${format(documentAccess.expiresAt, 'DD/MM/YYYY')}`}
			</Paragraph>
		);
	};

	return (
		<Flexbox direction='col'>
			<Flexbox items='center' justify='between' className='px-2 pt-2 pb-0.5 gap-2 dark:bg-card_dark'>
				<Flexbox justify='center' items='center' className='w-10 h-10 aspect-4/3 rounded-[4px] bg-n600_text'>
					<img src={documentType?.icon?.url} alt={name ?? ''} className='w-full h-full object-contain p-[5px]' />
				</Flexbox>

				<Flexbox direction='col' justify='center'>
					<Tooltip content={isCurvesDocumentView ? t('T_ACCESS_VIEW_ONLY_TOOLTIP') : t('T_OPEN_DOCUMENT')} style='dark'>
						<Paragraph
							className={`w-[150px] ${isCurvesDocumentView ? '' : 'cursor-pointer hover:underline'}`}
							onClick={isCurvesDocumentView ? () => {} : () => openSharedDocument({ documentId, documentTypeName: documentType?.title })}
						>
							{name}
						</Paragraph>
					</Tooltip>
				</Flexbox>
				<Flexbox items='center' justify='center' className='gap-2'>
					<Tooltip content={t('T_ACCESS_LEVEL')} style='dark'>
						<LabelBadge
							text={documentAccess.accessLevel === 'view' ? t('T_CAN_VIEW') : t('T_CAN_EDIT')}
							color='#000000'
							background='#00000020'
							className='max-w-5'
							pill
						/>
					</Tooltip>
					<Dropdown
						label={<AbbMoreDots size={24} fill={isDarkTheme ? '#f1f1f1' : '#1f1f1f'} className='cursor-pointer mr-1' />}
						inline
						isMenu
						arrowIcon={false}
						menuItems={menuItems}
					/>
				</Flexbox>
			</Flexbox>
			<Flexbox justify='between' direction='col' items='start' className='px-2 my-2 gap-0.5'>
				<Paragraph variant='text-sm'>{`${t('T_OWNER')}: ${documentAccess.ownerName}`}</Paragraph>
				<Paragraph variant='text-sm'>{`${t('T_PROJECT')}: ${project?.title}`}</Paragraph>
				<Paragraph>{renderPeriod()}</Paragraph>
			</Flexbox>
		</Flexbox>
	);
};
